import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get content() {
    return /** @type {HTMLElement | null} */ (
      this.details.querySelector('.details-content') ||
      Array.from(this.details.children).find((child) => child !== this.summary)
    );
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #disableAnimationOnDesktop() {
    return this.dataset.disableAnimationOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  /** @type {Animation | null} */
  #animation = null;

  /** @type {boolean} */
  #isClosing = false;

  /** @type {boolean} */
  #isExpanding = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    if (this.#animation) {
      this.#animation.cancel();
      this.#animation = null;
    }
  }

  /**
   * Handles the click event.
   * @param {MouseEvent} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser if disabled
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (this.#disableAnimationOnDesktop && isDesktop) {
      return;
    }

    // Check if the click is on the summary element
    const summary = event.target instanceof Element ? event.target.closest('summary') : null;
    if (!summary || summary !== this.summary) return;

    event.preventDefault();

    if (this.details.open) {
      this.collapse();
    } else {
      this.expand();
    }
  };

  /**
   * Smoothly expand the accordion content
   */
  expand() {
    const content = this.content;
    if (!content) {
      this.details.open = true;
      return;
    }

    if (this.details.open && !this.#isClosing) return;

    if (this.#animation) {
      this.#animation.cancel();
      this.#animation = null;
    }

    this.#isClosing = false;
    this.#isExpanding = true;

    if (!this.details.open) {
      this.details.open = true;
    }

    content.style.overflow = 'hidden';

    const startHeight = content.offsetHeight || 0;

    content.style.height = 'auto';
    const targetHeight = content.offsetHeight || content.scrollHeight;
    content.style.height = `${startHeight}px`;

    const duration = 240;
    const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';

    this.#animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: 0 },
        { height: `${targetHeight}px`, opacity: 1 }
      ],
      { duration, easing }
    );

    this.#animation.onfinish = () => {
      this.#animation = null;
      this.#isExpanding = false;
      content.style.overflow = '';
      content.style.height = '';
      content.style.opacity = '';
    };

    this.#animation.oncancel = () => {
      this.#animation = null;
      this.#isExpanding = false;
    };
  }

  /**
   * Smoothly collapse the accordion content
   */
  collapse() {
    const content = this.content;
    if (!content) {
      this.details.open = false;
      return;
    }

    if (!this.details.open && !this.#isExpanding) return;

    if (this.#animation) {
      this.#animation.cancel();
      this.#animation = null;
    }

    this.#isExpanding = false;
    this.#isClosing = true;

    content.style.overflow = 'hidden';

    const startHeight = content.offsetHeight || content.scrollHeight;
    const duration = 200;
    const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';

    this.#animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: 1 },
        { height: '0px', opacity: 0 }
      ],
      { duration, easing }
    );

    this.#animation.onfinish = () => {
      this.details.open = false;
      this.#animation = null;
      this.#isClosing = false;
      content.style.overflow = '';
      content.style.height = '';
      content.style.opacity = '';
    };

    this.#animation.oncancel = () => {
      this.#animation = null;
      this.#isClosing = false;
      content.style.overflow = '';
      content.style.height = '';
      content.style.opacity = '';
    };
  }

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.collapse();
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.collapse();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
